// A small "i" next to a label. Hovering it (or tapping it on a phone) opens a short
// explanation of what the number means. The bubble is drawn on top of the page, so a
// tile's rounded corners can't cut it off.
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from './i18n';

export default function InfoTip({ text, label }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const iconRef = useRef(null);
  const tipRef = useRef(null);
  const id = useId();

  useLayoutEffect(() => {
    if (!open || !iconRef.current || !tipRef.current) return;
    const r = iconRef.current.getBoundingClientRect();
    const tip = tipRef.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(r.left + r.width / 2 - tip.width / 2, window.innerWidth - tip.width - 8));
    const above = r.top - tip.height - 8;
    setPos({ left, top: above >= 8 ? above : r.bottom + 8 });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (e.type !== 'keydown' || e.key === 'Escape') setOpen(false); };
    const outside = (e) => { if (iconRef.current && !iconRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', close);
    document.addEventListener('pointerdown', outside);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', close);
      document.removeEventListener('pointerdown', outside);
    };
  }, [open]);

  // A tap on a phone fires hover, focus and click together, so a click right after
  // opening keeps it open instead of closing it again.
  const openedAt = useRef(0);
  const show = () => {
    if (open) return;
    openedAt.current = Date.now();
    setPos(null);
    setOpen(true);
  };
  return (
    <>
      <button type="button" ref={iconRef} className="info-tip" aria-label={label ? t('What is {label}?', { label }) : t('More info')}
        aria-describedby={open ? id : undefined}
        onMouseEnter={show} onMouseLeave={() => setOpen(false)} onFocus={show} onBlur={() => setOpen(false)}
        onClick={(e) => { e.stopPropagation(); if (open && Date.now() - openedAt.current > 400) setOpen(false); else show(); }}>
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="8" cy="8" r="6.6" />
          <line x1="8" y1="7.2" x2="8" y2="11.4" />
          <circle cx="8" cy="4.9" r="0.6" className="info-tip-dot" />
        </svg>
      </button>
      {open && createPortal(
        <div ref={tipRef} id={id} role="tooltip" className="info-tip-bubble"
          style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: 'hidden' }}>
          {text}
        </div>,
        document.body,
      )}
    </>
  );
}
